import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Search, SlidersHorizontal, X } from "lucide-react";
import SliderArrow from "./SliderArrow";
import BrandArrow from "@/components/BrandArrow";
import DevelopmentCaseDetails, { type DevelopmentDetails } from "@/components/DevelopmentCaseDetails";

export type CaseMetric = { value: string; label: string };

export type Case = {
  id: string;
  client: string;
  format: string;
  audience: string;
  request: string;
  result: string;
  short: string;
  tagLabel?: string;
  /** Категории для фильтра секции «Нам доверяют». По умолчанию — [tagLabel ?? format].
      Для кейсов с несколькими форматами (напр. R1: стратсессия + обучение) перечисляем все. */
  filterTags?: string[];
  photo?: string;
  photoPos?: string;
  /** Логотип компании — показываем на белой «плашке» по центру тёмной карточки
      для кейсов, у которых нет фото. */
  logo?: string;
  /** Карточка с крупной ключевой метрикой вместо фото. */
  featured?: boolean;
  metrics?: CaseMetric[];
  fieldLabels?: FieldLabels;
  developmentDetails?: DevelopmentDetails;
};

export type FieldLabels = {
  audience: string;
  request: string;
  result: string;
};

// Gradient background per format — dark, editorial mood like reference
const formatVisuals: Record<string, { gradient: string; glow: string }> = {
  "Стратегическая сессия": {
    gradient:
      "radial-gradient(120% 80% at 30% 20%, hsl(20 45% 22%) 0%, hsl(20 25% 10%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(11 95% 52% / 0.25)",
  },
  "Обучение": {
    gradient:
      "radial-gradient(120% 80% at 50% 80%, hsl(210 30% 18%) 0%, hsl(220 20% 10%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(11 95% 52% / 0.18)",
  },
  "AI-трансформация": {
    gradient:
      "radial-gradient(120% 80% at 20% 80%, hsl(11 65% 30%) 0%, hsl(11 40% 14%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(11 95% 55% / 0.35)",
  },
};

// Fallback palette for arbitrary formats (implementation industries)
const fallbackPalette: { gradient: string; glow: string }[] = [
  {
    gradient:
      "radial-gradient(120% 80% at 30% 20%, hsl(20 45% 22%) 0%, hsl(20 25% 10%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(11 95% 52% / 0.25)",
  },
  {
    gradient:
      "radial-gradient(120% 80% at 70% 30%, hsl(210 30% 22%) 0%, hsl(220 20% 11%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(210 80% 55% / 0.22)",
  },
  {
    gradient:
      "radial-gradient(120% 80% at 50% 80%, hsl(160 30% 18%) 0%, hsl(170 20% 10%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(160 70% 45% / 0.22)",
  },
  {
    gradient:
      "radial-gradient(120% 80% at 20% 80%, hsl(11 65% 30%) 0%, hsl(11 40% 14%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(11 95% 55% / 0.32)",
  },
  {
    gradient:
      "radial-gradient(120% 80% at 70% 70%, hsl(270 30% 22%) 0%, hsl(265 20% 11%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(270 70% 60% / 0.22)",
  },
  {
    gradient:
      "radial-gradient(120% 80% at 30% 70%, hsl(45 45% 24%) 0%, hsl(40 25% 12%) 45%, hsl(0 0% 6%) 100%)",
    glow: "hsl(40 90% 55% / 0.22)",
  },
];

const hash = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const getVisual = (format: string) =>
  formatVisuals[format] ?? fallbackPalette[hash(format) % fallbackPalette.length];

const caseCategories = [
  { label: "Все", value: "Все" },
  { label: "AI-трансформация", value: "AI-трансформация" },
  { label: "Стратегические сессии", value: "Стратегическая сессия" },
  { label: "Обучение", value: "Обучение" },
  { label: "Разработка", value: "Разработка" },
];

type Props = {
  items?: Case[];
  fieldLabels?: FieldLabels;
  ctaLabel?: string;
  ctaHref?: string;
  showFormatTags?: boolean;
  showFilters?: boolean;
  layout?: "carousel" | "grid";
};

const defaultLabels: FieldLabels = {
  audience: "Целевая аудитория",
  request: "Запрос перед обучением",
  result: "Результаты обучения",
};

import trainingData from "@/data/training-cases.json";

const TrainingCasesSection = ({
  items,
  fieldLabels = defaultLabels,
  ctaLabel = "Обсудить похожий проект",
  ctaHref = "#contact",
  showFormatTags = true,
  showFilters = false,
  layout = "carousel",
}: Props) => {
  const allCases = useMemo(
    () => ((items ?? (trainingData as Case[])) as Case[]),
    [items],
  );
  const [activeFilter, setActiveFilter] = useState("Все");
  const [searchQuery, setSearchQuery] = useState("");
  const filterDetailsRef = useRef<HTMLDetailsElement>(null);
  const cases = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase("ru");
    return allCases.filter((item) => {
      const matchesCategory = activeFilter === "Все" || (item.developmentDetails ? "Разработка" : item.format) === activeFilter;
      const matchesSearch = !query || [item.client, item.format, item.tagLabel, item.short, item.audience, item.request, item.result, item.developmentDetails?.title, item.developmentDetails?.customer, item.developmentDetails?.technologies.join(" "), item.developmentDetails?.evidence, item.developmentDetails?.agents?.map(agent => `${agent.title} ${agent.description}`).join(" ")].filter(Boolean).join(" ").toLocaleLowerCase("ru").includes(query);
      return matchesCategory && matchesSearch;
    });
  }, [activeFilter, allCases, searchQuery]);

  const [openId, setOpenId] = useState<string | null>(null);
  const active = cases.find((c) => c.id === openId) ?? null;
  const isGrid = layout === "grid";
  const cardLayoutClass = isGrid
    ? "w-full min-w-0 h-[400px] xl:h-[440px]"
    : "shrink-0 snap-start w-[82vw] xs:w-[68vw] sm:w-[52vw] md:w-[360px] lg:w-[380px] h-[440px] md:h-[480px]";
  const cardTitleClass = isGrid
    ? "text-[23px] leading-[1.12] xl:text-[25px]"
    : "text-[28px] leading-tight md:text-3xl";

  // Блокируем скролл body, пока открыта модалка (модалка рендерится порталом в body).
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenId(null);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  const trackRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [cases]);

  // Reset scroll when items change (mode switch)
  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: 0 });
    updateArrows();
  }, [items]);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const first = el.querySelector<HTMLElement>("[data-case-card]");
    const cardWidth = first?.offsetWidth ?? 280;
    const gap = 20;
    el.scrollBy({ left: dir * (cardWidth + gap) * 1, behavior: "smooth" });
  };

  return (
    <section className="pt-12 md:pt-16 pb-16 md:pb-24 relative">
      {/* md:px-16 — резервируем боковые «дорожки» под стрелки, чтобы они стояли
          ЗА пределами карточек, а не наезжали на них. */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 relative ${isGrid ? "" : "md:px-16"}`}>
        {showFilters && (
          <div className="mb-7 flex items-center gap-3">
            <label className="flex h-[50px] w-full max-w-[460px] items-center gap-3 rounded-2xl border border-border bg-card px-[18px] focus-within:border-accent">
              <Search size={20} className="shrink-0 text-muted-foreground" aria-hidden="true" />
              <input type="search" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Поиск кейсов..." aria-label="Поиск кейсов" className="h-full w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground" />
            </label>
            <details className="relative" ref={filterDetailsRef} onKeyDown={event => { if (event.key === "Escape") event.currentTarget.open = false; }}>
              <summary aria-label="Категории кейсов" className={`flex size-[50px] cursor-pointer list-none items-center justify-center rounded-2xl border bg-card transition-colors hover:border-accent/50 [&::-webkit-details-marker]:hidden ${activeFilter === "Все" ? "border-border text-foreground" : "border-accent text-accent"}`}>
                <SlidersHorizontal size={20} aria-hidden="true" />
              </summary>
              <div id="case-category-menu" role="menu" aria-label="Категории кейсов" className="absolute right-0 top-[calc(100%+8px)] z-40 w-[230px] rounded-2xl border border-border bg-card p-2 shadow-xl">
                {caseCategories.map(category => <button key={category.value} type="button" role="menuitemradio" aria-checked={activeFilter === category.value} onClick={() => { setActiveFilter(category.value); if (filterDetailsRef.current) filterDetailsRef.current.open = false; }} className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-foreground hover:bg-muted">
                  <span>{category.label}</span>{activeFilter === category.value && <Check size={16} className="text-accent" aria-hidden="true" />}
                </button>)}
              </div>
            </details>
          </div>
        )}
        {/* Arrows — в боковых дорожках, вне карточек */}
        {!isGrid && <SliderArrow
          dir="left"
          onClick={() => scrollBy(-1)}
          disabled={!canLeft}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20"
        />}
        {!isGrid && <SliderArrow
          dir="right"
          onClick={() => scrollBy(1)}
          disabled={!canRight}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20"
        />}

        {/* На мобильном карточки «в край» (bleed), на md+ — ровно в контент-боксе,
            освобождая дорожки под стрелки (md:mx-0 md:px-0). */}
        {cases.length === 0 && <p className="py-12 text-muted-foreground">Кейсы не найдены.</p>}
        <div
          ref={trackRef}
          className={isGrid
            ? "grid grid-cols-1 gap-4 py-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5"
            : "flex gap-4 overflow-x-auto overflow-y-hidden overscroll-x-contain snap-x snap-mandatory scroll-smooth py-4 -mx-4 px-4 sm:-mx-6 sm:px-6 md:mx-0 md:gap-5 md:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"}
        >
          {cases.map((c, idx) => {
            const v = getVisual(c.format);

            if (c.format === "AI-трансформация" && c.featured && c.metrics?.length) {
              const metric = c.metrics[0];
              return (
                <motion.button
                  key={c.id}
                  data-case-card
                  type="button"
                  onClick={() => setOpenId(c.id)}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: (idx % 4) * 0.05 }}
                  whileHover={{ y: -4 }}
                  className={`group relative text-left rounded-3xl overflow-hidden ${cardLayoutClass} bg-[hsl(220,20%,8%)] border border-accent/40 hover:border-accent/70 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
                  aria-label={`Открыть кейс: ${c.client}`}
                >
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(70% 50% at 80% 0%, hsl(11 95% 55% / 0.28), transparent 60%), radial-gradient(60% 45% at 10% 100%, hsl(11 95% 55% / 0.12), transparent 60%)",
                    }}
                  />
                  <div className="relative z-10 h-full flex flex-col p-5 md:p-6">
                    <div className="flex items-start gap-3">
                      {showFormatTags && (
                        <span className={isGrid
                          ? "inline-block rounded border border-border/50 bg-background/80 px-2.5 py-1 font-mono text-sm font-semibold tracking-widest text-foreground backdrop-blur-sm"
                          : "inline-block rounded-full bg-accent/10 border border-accent/40 px-3.5 py-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.14em] text-accent font-heading"}>
                          {c.tagLabel ?? c.format}
                        </span>
                      )}
                    </div>

                    <h3 className={`mt-4 font-heading text-white tracking-tight [hyphens:auto] [overflow-wrap:anywhere] ${cardTitleClass}`}>
                      {c.client}
                    </h3>
                    <p className="mt-2 text-white/60 text-sm md:text-[15px] leading-snug line-clamp-2">
                      {c.short}
                    </p>

                    <div className="mt-auto">
                      <div className="text-[11px] font-heading uppercase tracking-[0.18em] text-white/45">
                        {metric.label}
                      </div>
                      <div className="mt-2 text-accent font-heading font-bold leading-[1.05] tracking-normal text-[30px] [overflow-wrap:anywhere] md:text-[34px]">
                        {metric.value}
                      </div>
                    </div>

                    <div className="pt-4">
                      <span className="inline-flex items-center gap-1.5 self-start text-[13px] font-semibold leading-none text-white/85 transition-colors group-hover:text-white">
                        <span className="optical-center">Подробнее</span>
                        <BrandArrow className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </motion.button>
              );
            }

            return (
              <motion.button
                key={c.id}
                data-case-card
                type="button"
                onClick={() => setOpenId(c.id)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (idx % 4) * 0.05 }}
                whileHover={{ y: -4 }}
                className={`group relative text-left rounded-3xl overflow-hidden ${cardLayoutClass} focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
                style={c.photo ? undefined : { background: v.gradient }}
                aria-label={`Открыть кейс: ${c.client}`}
              >
                {c.photo ? (
                  <>
                    <img
                      src={c.photo}
                      alt={c.client}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{ objectPosition: c.photoPos ?? "center" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
                  </>
                ) : (
                  <>
                    {/* Ambient glow */}
                    <div
                      className="absolute inset-0 opacity-70 transition-opacity duration-500 group-hover:opacity-100 pointer-events-none"
                      style={{
                        background: `radial-gradient(60% 40% at 50% 100%, ${v.glow} 0%, transparent 70%)`,
                      }}
                    />
                    {/* Subtle grid texture */}
                    <div
                      className="absolute inset-0 opacity-[0.06] pointer-events-none"
                      style={{
                        backgroundImage:
                          "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
                        backgroundSize: "40px 40px",
                      }}
                    />
                  </>
                )}

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-5 md:p-6">
                  <div>
                    {showFormatTags && (
                      <span className={isGrid
                        ? "inline-block rounded border border-border/50 bg-background/80 px-2.5 py-1 font-mono text-sm font-semibold tracking-widest text-foreground backdrop-blur-sm"
                        : "inline-block rounded-full bg-black/45 backdrop-blur px-3.5 py-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.14em] text-white/95 font-heading border border-white/10"}>
                        {c.tagLabel ?? c.format}
                      </span>
                    )}
                  </div>

                  <div className="space-y-3">
                    <h3 className={`font-heading text-white tracking-tight [hyphens:auto] [overflow-wrap:anywhere] ${cardTitleClass}`}>
                      {c.client}
                    </h3>
                    <p className="text-white/70 text-sm md:text-[15px] leading-snug line-clamp-3">
                      {c.short}
                    </p>
                    <div className="pt-2">
                      <span className="inline-flex items-center gap-1.5 self-start text-[13px] font-semibold leading-none text-white/85 transition-colors group-hover:text-white">
                        <span className="optical-center">Подробнее</span>
                        <BrandArrow className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Modal — рендерим порталом в body, чтобы position:fixed считался от вьюпорта,
          а не от трансформированного предка (иначе окно «уезжает» в середину страницы). */}
      {createPortal(
        <AnimatePresence>
          {active && (
            <motion.div
              className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setOpenId(null)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label={`Кейс: ${active.client}`}
                initial={{ y: 40, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 40, opacity: 0, scale: 0.98 }}
                transition={{ type: "spring", damping: 28, stiffness: 260 }}
                className="relative z-10 w-full sm:max-w-6xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden rounded-t-3xl sm:rounded-3xl bg-card shadow-2xl flex flex-col sm:flex-row"
              >
                {/* Кнопка закрытия — светлый чип, читается и на фото, и на белом */}
                <button
                  type="button"
                  onClick={() => setOpenId(null)}
                  aria-label="Закрыть"
                  className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-background/85 backdrop-blur border border-border text-foreground flex items-center justify-center transition-colors hover:bg-background"
                >
                  <X size={18} />
                </button>

                {active.developmentDetails ? (
                  <DevelopmentCaseDetails
                    details={active.developmentDetails}
                    task={active.audience}
                    solution={active.request}
                    result={active.result}
                    ctaLabel={ctaLabel}
                    ctaHref={ctaHref}
                    onClose={() => setOpenId(null)}
                  />
                ) : (
                  <>
                {/* Левая колонка — визуал (фото или градиент) + название кейса */}
                <div
                  className="relative shrink-0 h-56 sm:h-auto sm:w-[52%] min-h-[220px] overflow-hidden"
                  style={active.photo ? undefined : { background: getVisual(active.format).gradient }}
                >
                  {active.photo ? (
                    <img
                      src={active.photo}
                      alt={active.client}
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{ objectPosition: active.photoPos ?? "center" }}
                    />
                  ) : (
                    <>
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `radial-gradient(60% 45% at 50% 100%, ${getVisual(active.format).glow} 0%, transparent 70%)`,
                        }}
                      />
                    </>
                  )}
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/5 pointer-events-none"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                    <h3 className="font-heading text-white text-2xl md:text-[28px] leading-tight tracking-tight [hyphens:auto] [overflow-wrap:anywhere] [text-wrap:balance]">
                      {active.client}
                    </h3>
                  </div>
                </div>

                {/* Правая колонка — текст, скроллится */}
                <div className="relative flex-1 min-w-0 overflow-y-auto px-6 md:px-10 py-7 md:py-9 space-y-7">
                  {active.metrics?.length && (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-5 pb-1">
                      {active.metrics.map((m) => (
                        <div key={m.label}>
                          <div className="font-heading font-bold text-accent leading-none tracking-tight text-2xl md:text-[28px]">
                            {m.value}
                          </div>
                          <div className="mt-1.5 text-xs md:text-sm text-muted-foreground leading-snug">
                            {m.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Field label={(active.fieldLabels ?? fieldLabels).audience} value={active.audience} />
                  <Field label={(active.fieldLabels ?? fieldLabels).request} value={active.request} />
                  <Field label={(active.fieldLabels ?? fieldLabels).result} value={active.result} />

                  <div className="pt-1">
                    <a
                      href={ctaHref}
                      onClick={() => setOpenId(null)}
                      className="inline-flex items-center rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-body hover:opacity-90 transition-opacity"
                    >
                      {ctaLabel}
                    </a>
                  </div>
                </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </section>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <h4 className="font-heading text-foreground text-lg md:text-xl mb-2 tracking-tight">
      {label}
    </h4>
    <p className="text-text-subtle text-[15px] md:text-base leading-relaxed whitespace-pre-line">
      {value}
    </p>
  </div>
);

export default TrainingCasesSection;
